// Import Libraries
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

// Import JS Modules
import { builderDispatch, WebUI, PrismUI } from '../../_utils/ui';
import getImageSources from '../image-sources';
import notifications from '../utils/notifications';
import LoaderAdditionalOptions from '../additional-options';
import ImageUploadFileValidator from '../../_utils/image-upload-file-validator';

const LoaderBase = {
  getChildContext() {
    const { additionalOptions = {} } = this.state;

    return {
      additionalOptions,
    };
  },

  componentDidMount() {
    window.addEventListener('orientationchange', this.handleOrientationChanged);
    window.addEventListener('keydown', this.handleKeyDown);

    this._isMounted = true;

    this.builderDispatchToken = builderDispatch.register(payload => {
      window.requestAnimationFrame(() => {
        switch (payload.type) {
          case PrismUI.ADD_PHOTOS:
            this.begin(
              payload.upload_url,
              payload.excludeServices,
              payload.callToActionButtonText,
              payload.callToActionButtonEvent,
              payload.additionalOptions
            );
            break;

          case WebUI.UPLOAD_SHOW_FAILURES:
            this.errorMessage = payload.message;

            if (payload.number_of_errors) {
              this.number_of_errors =
                this.number_of_errors + payload.number_of_errors;
            }

            this.showFailures();
            break;

          case PrismUI.CLOSE_ADD_PHOTOS_MODAL:
            this.close();
            break;

          case PrismUI.CLOUD_SERVICE_HEADER:
            this.setState({ serviceHeaderMounted: payload.mounted });
            break;

          case WebUI.UPLOAD_ERROR:
          case WebUI.UPLOAD_BEGUN:
          case PrismUI.IMAGES_UPDATED:
            this.setState({ loading: false });
            break;
        }
      });
    });

    $(window)
      .on('offline', this.handleGoneOffline)
      .on('online', this.handleGoneOnline);

    $(ReactDOM.findDOMNode(this))
      .modal({
        show: false,
      })
      .on('hide.bs.modal', e => {
        const { showUploadErrorsModal, showFailures } = this.state;

        // A hide is now animating, so open() must queue rather than issue a
        // show() Bootstrap would drop after creating its backdrop.
        this._hiding = true;

        if (showUploadErrorsModal) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.toggleUploadErrorsModal();

          // Prevented: nothing animates and no `hidden` follows, so clear the
          // flag here or every later open() would queue forever.
          this._hiding = false;

          return false;
        }

        this.setState({ focus: false, uploadErrors: [], showFailures: false });

        // Escape and the backdrop dismiss the failures modal too, and the album page
        // only reveals what uploaded when it sees UPLOAD_IGNORE_FAILURES - so every
        // dismissal goes through the same handler, not just the button.
        // performUpload closes the loader as well, and that must not be read as a
        // dismissal: aborting would clear the files the retry is about to send.
        if (showFailures && !this._startingUpload) {
          this.handleIgnoreFailures();
        }
      })
      .on('hidden.bs.modal', () => {
        this._hiding = false;

        // Bootstrap has finished tearing down, so a show queued during the hide
        // can run now and get its own backdrop.
        if (this._showAfterHide) {
          this._showAfterHide = false;
          this.open();
        }

        builderDispatch.dispatch({
          type: WebUI.UPLOAD_CLOSED,
        });
      });

    $(ReactDOM.findDOMNode(this)).on('shown.bs.modal', () => {
      this.setState({ focus: true });
    });
  },

  componentWillUnmount() {
    this._isMounted = false;
    builderDispatch.unregister(this.builderDispatchToken);

    $(window)
      .off('offline', this.handleGoneOffline)
      .off('online', this.handleGoneOnline);

    window.removeEventListener(
      'orientationchange',
      this.handleOrientationChanged
    );
  },

  // Bootstrap ignores a show() issued while a hide is still animating - but only
  // after it has created the backdrop, leaving an orphan that is invisible (no
  // `show` class, so opacity 0) yet still covers the viewport and swallows every
  // click until the page is reloaded. The upload flow issues exactly that pair:
  // performUpload closes the loader, and a failed batch re-opens it from
  // UPLOAD_SHOW_FAILURES a moment later. So the two are serialised here instead
  // of being fired straight at Bootstrap.
  open() {
    if (!this._isMounted) return;

    if (this._hiding) {
      this._showAfterHide = true;
      return;
    }

    $(ReactDOM.findDOMNode(this)).modal('show');
  },

  close() {
    if (!this._isMounted) return;

    this.setState(() => ({ uploadErrors: [] }));

    // A close cancels a show that was waiting on this hide: the last command
    // wins, rather than the loader springing back open after being dismissed.
    this._showAfterHide = false;

    $(ReactDOM.findDOMNode(this)).modal('hide');
  },

  begin(
    url,
    excludeServices = [],
    callToActionButtonText,
    callToActionButtonEvent,
    additionalOptions = {}
  ) {
    const { loading } = this.state;
    if (!App.order.storage_provider_present) {
      excludeServices.push('albums');
    }

    if (loading) return this.close();

    this.clearAll();
    this.setState(
      ({ target_album: prevTargetAlbum }) => {
        const extraFileTypes =
          additionalOptions[LoaderAdditionalOptions.EXTRA_FILE_TYPES] || [];
        return {
          upload_url: url,
          excludeServices: [].concat(excludeServices),
          callToActionButtonText,
          callToActionButtonEvent,
          additionalOptions,
          hideMenu: this.state.serviceHeaderMounted,
          uploadSource: additionalOptions.uploadSource,
          target_album:
            additionalOptions[LoaderAdditionalOptions.TARGET_ALBUM] ||
            prevTargetAlbum,
          fileUploadValidator: new ImageUploadFileValidator({
            extraFileTypes: extraFileTypes,
          }),
        };
      },
      () => {
        this.open();
      }
    );
  },

  handleSourceSelect(source_index, service) {
    this.setState({ source: source_index });
    notifications.service(service);
  },

  previews() {
    const imageSources = getImageSources();
    return imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (implementation) {
        memo = memo.concat(implementation.filesSelected());
      }
      return memo;
    }, []);
  },

  handleFilesSelected() {
    const {
      additionalOptions = {},
      source: sourceIdx,
    } = this.state;

    const imageSources = getImageSources();
    const source = imageSources[sourceIdx]?.component,
          sourceAutoUploads = source?.prototype?.[LoaderAdditionalOptions.CLOSE_ON_FILE_SELECTION],
          pageAutoUploads = additionalOptions[LoaderAdditionalOptions.CLOSE_ON_FILE_SELECTION];

    if (pageAutoUploads || sourceAutoUploads) {
      this.handleAddImages();
    }

    this.forceUpdate();
  },

  handleImagePreviewError(file) {
    if (!this.state.uploadErrors.includes(file)) {
      this.setState(prevState => ({
        uploadErrors: [...prevState.uploadErrors, file],
      }));
    }
  },

  toggleUploadErrorsModal() {
    this.setState(prevState => ({
      showUploadErrorsModal: !prevState.showUploadErrorsModal,
    }));
  },

  removeAllUploadErrorsClick() {
    this.filterBrokenImages();

    this.setState(() => ({ uploadErrors: [], showUploadErrorsModal: false }));
  },

  handleRemovePreview(file) {
    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PHOTO_REMOVED_FROM_TRAY,
      key: file.originalKey || file.url,
      service: file.service,
    });

    const implementation = this.refs[file.service];

    if (implementation) {
      implementation.removeFile(file);
    }

    if (this.state.uploadErrors) {
      this.setState(prevState => {
        const result = prevState.uploadErrors.filter(error => error !== file);
        return { uploadErrors: result };
      });
    }

    this.forceUpdate();
  },

  // Files that this pass will actually send. A source keeps its files after
  // uploading them (they are only flagged `uploaded`), and the uploaders skip
  // flagged files, so counting every selected file would announce a batch that
  // can never complete.
  pendingNumberOfFilesToUpload() {
    const imageSources = getImageSources();
    return imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (!implementation) {
        return memo;
      }

      // filesSelected() reads the live list; a wrapper source such as
      // CombinedLoader holds `files` as a snapshot that can lag behind it.
      const selected = implementation.filesSelected
        ? implementation.filesSelected()
        : implementation.files;

      // Guard the list we are about to read, not `implementation.files`. A freshly
      // mounted CombinedLoader has no `files` of its own - setFilesCallback only
      // fires when a user picks files - so guarding on it skipped the source
      // outright after the failures view remounted the sources. The batch then
      // counted zero and performUpload returned at its silent guard: retry did
      // nothing at all.
      if (selected) {
        memo += selected.filter((file) => !file.uploaded).length;
      }

      return memo;
    }, 0);
  },

  performUpload(upload_url = this.state.upload_url, additionalData) {
    this._startingUpload = true;
    this.close();
    this._startingUpload = false;

    // needs to change to CTABE when defined.
    notifications.clickEvent(this.state.callToActionButtonEvent);

    this.uploaded_index = 0;
    this.number_of_errors = 0;
    this.batchDone = false;
    this.batchErrorMessage = '';
    this.expectedUploads = this.pendingNumberOfFilesToUpload();

    if (!this.expectedUploads) {
      return;
    }

    builderDispatch.delayedDispatch({
      type: WebUI.UPLOAD_BEGUN,
      number_of_files: this.expectedUploads,
      ...additionalData,
    });

    const imageSources = getImageSources();
    imageSources.forEach((source) => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.performUpload(
          upload_url,
          additionalData,
          this.handleFileUploaded,
          this.handleFileUploadError,
        );
      }
    });
  },

  // The batch size announced by performUpload. Recomputing it here would drift
  // as files settle, so completion is measured against what was announced.
  expectedNumberOfUploads() {
    return this.expectedUploads || this.pendingNumberOfFilesToUpload();
  },

  // Completion is announced once per batch. `>=` keeps an over-delivering batch
  // from counting past its target and never finishing, and batchDone keeps the
  // callbacks after that from announcing it again — UPLOAD_DONE is not
  // idempotent downstream (it re-runs the basket reconciliation, and with
  // errors present it re-opens the failures modal).
  announceBatchDone(payload) {
    if (this.batchDone) {
      return;
    }

    this.batchDone = true;
    builderDispatch.dispatch(payload);
  },

  handleFileUploaded(data, source) {
    const expectedNumberOfUploads = this.expectedNumberOfUploads();

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PROGRESS,
      data,
      source,
      number_of_files: expectedNumberOfUploads,
    });

    if (++this.uploaded_index >= expectedNumberOfUploads) {
      this.announceBatchDone({
        type: WebUI.UPLOAD_DONE,
        number_of_files: expectedNumberOfUploads,
        number_of_errors: this.number_of_errors,
        // Whichever file settles last completes the batch, so the message has to
        // come from the batch rather than from this callback.
        message: this.batchErrorMessage,
      });
    }
  },

  handleFileUploadError(message) {
    const expectedNumberOfUploads = this.expectedNumberOfUploads();

    // Keep the first actionable message the batch produced; a later failure
    // without one must not overwrite it.
    if (message && !this.batchErrorMessage) {
      this.batchErrorMessage = message;
    }

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_ERROR,
      number_of_errors: ++this.number_of_errors,
    });

    if (++this.uploaded_index >= expectedNumberOfUploads) {
      this.announceBatchDone({
        type: WebUI.UPLOAD_DONE,
        number_of_files: expectedNumberOfUploads,
        number_of_errors: this.number_of_errors,
        message: this.batchErrorMessage,
      });
    }
  },

  handleGoneOffline() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.pause();
      }
    });

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_PAUSED,
    });
  },

  handleGoneOnline() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.restart();
      }
    });

    builderDispatch.dispatch({
      type: WebUI.UPLOAD_RESUMED,
    });
  },

  handleIgnoreFailures() {
    // Reached from the button and from hide.bs.modal, and the button's own abort
    // closes the modal - so without this the dismissal would be announced twice.
    if (this._failuresDismissed) return;

    this._failuresDismissed = true;

    // close() can be reached from inside a dispatch (PrismUI.CLOSE_ADD_PHOTOS_MODAL)
    // and Flux refuses a nested one, so this goes through delayedDispatch - which
    // defers only when isDispatching() and carries the MAX_DELAYED_DISPATCHES guard.
    builderDispatch.delayedDispatch({ type: WebUI.UPLOAD_IGNORE_FAILURES });

    this.handleAbort();
  },

  handleAbort() {
    this.setState(
      {
        uploadErrors: [],
        showFailures: false,
      },
      () => {
        this.close();

        window.requestAnimationFrame(() => {
          builderDispatch.dispatch({
            type: WebUI.UPLOAD_ABORTED,
          });
        });

        this.clearAll();
      }
    );
  },

  handleOrientationChanged() {
    setTimeout(this.setWindowSize, 100);
  },

  showFailures() {
    const imageSources = getImageSources();

    this._filesToRetry = {};
    this._failuresDismissed = false;

    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.clearUploaded();

        // clearUploaded() drops the files that made it, so what is left is
        // exactly the batch's failures - snapshot it before the modal remounts
        // the sources and takes the components holding those files with it.
        // Same wrapper-source fallback as pendingNumberOfFilesToUpload: a
        // CombinedLoader has no filesSelected() and would otherwise retry nothing.
        const failed = implementation.filesSelected
          ? implementation.filesSelected()
          : implementation.files;

        if (failed) {
          this._filesToRetry[source.service] = [...failed];
        }
      }
    });

    this.setState(
      {
        showFailures: true,
      },
      () => {
        this.open();
      }
    );
  },

  handleRetryFailures() {
    const filesToRetry = this._filesToRetry || {};

    this.setState({ showFailures: false }, () => {
      getImageSources().forEach((source) => {
        const implementation = this.refs[source.service];
        const files = filesToRetry[source.service];

        if (implementation && files && files.length) {
          // restoreFiles reaches the component the uploader actually reads from.
          // A wrapper source such as CombinedLoader holds `files` as a snapshot
          // while filesSelected() reads its inner loader, so assigning here would
          // restore a list performUpload never sees - it would then count zero
          // files, return at its silent guard, and the failures would be lost.
          if (typeof implementation.restoreFiles === 'function') {
            implementation.restoreFiles(files);
          } else {
            implementation.files = files;
          }
        }
      });

      this._filesToRetry = null;
      this.handleAddImages();
    });
  },

  clearAll() {
    const imageSources = getImageSources();
    imageSources.forEach(source => {
      const implementation = this.refs[source.service];
      if (implementation) {
        implementation.clear();
      }
    });
  },

  handleTargetAlbumSelected(album) {
    this.setState({
      target_album: album,
    });
  },

  getWindowSize() {
    return {
      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,
    };
  },

  setWindowSize() {
    this.setState(this.getWindowSize);
  },

  filterBrokenImages() {
    const { uploadErrors } = this.state;

    const imageSources = getImageSources();
    imageSources.reduce((memo, source) => {
      const implementation = this.refs[source.service];
      if (
        implementation &&
        !!implementation.files &&
        implementation.files.length > 0
      ) {
        implementation.files = implementation.files.filter(
          file => !uploadErrors.includes(file)
        );
      }
      return memo;
    }, []);
  },
};

LoaderBase.childContextTypes = {
  additionalOptions: PropTypes.object,
};

export default LoaderBase;
